import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Download, FileText, Clock, AlertTriangle, Activity, DollarSign } from 'lucide-react';
import { reportsApi, equipmentApi, type DowntimeReport, type DowntimeLogEntry } from '@/lib/api';
import { exportToExcel, exportToCsv } from '@/lib/excel';
import { Select } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { KpiPanel } from '@/components/KpiPanel';
import { Badge } from '@/components/ui/badge';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMins(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function fmtMonth(iso: string): string {
  const [year, month] = iso.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

const TASK_TYPE_COLORS: Record<string, string> = {
  corrective:  '#ef4444',
  preventive:  '#3b82f6',
  predictive:  '#8b5cf6',
  unlinked:    '#94a3b8',
};

const BAR_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6'];

// ── Build shared log rows (reused by Excel/CSV) ───────────────────────────────

function buildLogRows(data: DowntimeReport) {
  return data.log.map(e => ({
    wo_number:        e.wo_number,
    title:            e.title,
    equipment:        `${e.asset_tag} — ${e.equipment_name}`,
    work_type:        e.task_type ?? '',
    date:             e.actual_end ? new Date(e.actual_end).toLocaleDateString() : '',
    downtime_minutes: e.downtime_minutes,
    downtime_hours:   parseFloat((e.downtime_minutes / 60).toFixed(2)),
    cost_usd:         e.cost ?? '',
    status:           e.status,
  }));
}

function buildSummaryRows(data: DowntimeReport) {
  return [
    { section: 'SUMMARY',      metric: 'Total Downtime',    value: fmtMins(data.summary.total_downtime_minutes) },
    { section: '',             metric: 'Total Incidents',   value: String(data.summary.total_incidents) },
    { section: '',             metric: 'Avg Per Incident',  value: fmtMins(data.summary.avg_downtime_per_incident) },
    { section: '',             metric: 'Total Cost ($)',    value: String(data.summary.total_cost) },
    { section: '',             metric: '',                  value: '' },
    { section: 'BY EQUIPMENT', metric: 'Equipment',         value: 'Downtime (min) | Incidents' },
    ...data.by_equipment.map(e => ({
      section: '',
      metric:  `${e.asset_tag} — ${e.equipment_name}`,
      value:   `${e.downtime_minutes} min | ${e.incidents}`,
    })),
    { section: '', metric: '', value: '' },
    { section: 'BY MONTH', metric: 'Month', value: 'Downtime (min) | Incidents' },
    ...data.by_month.map(m => ({
      section: '',
      metric:  fmtMonth(m.month),
      value:   `${m.downtime_minutes} min | ${m.incidents}`,
    })),
  ];
}

function exportExcel(data: DowntimeReport, preset: string) {
  const filename = `downtime_report_${preset}_${new Date().toISOString().slice(0, 10)}`;
  exportToExcel(buildSummaryRows(data), filename + '_summary');
  exportToExcel(buildLogRows(data), filename + '_log');
}

function exportCsv(data: DowntimeReport, preset: string) {
  const filename = `downtime_report_${preset}_${new Date().toISOString().slice(0, 10)}`;
  exportToCsv(buildSummaryRows(data), filename + '_summary');
  // slight delay so both downloads don't collide
  setTimeout(() => exportToCsv(buildLogRows(data), filename + '_log'), 300);
}

async function exportPdf(
  reportRef: React.RefObject<HTMLDivElement | null>,
  preset: string,
) {
  const element = reportRef.current;
  if (!element) return;

  const { default: html2canvas } = await import('html2canvas');
  const { jsPDF } = await import('jspdf');

  const canvas = await html2canvas(element, {
    scale:           2,
    useCORS:         true,
    backgroundColor: '#ffffff',
    logging:         false,
  });

  const imgData   = canvas.toDataURL('image/png');
  const pageW     = 210; // A4 mm
  const pageH     = 297;
  const margin    = 10;
  const printW    = pageW - margin * 2;
  const imgH      = (canvas.height * printW) / canvas.width;

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Header
  pdf.setFontSize(16);
  pdf.setTextColor(15, 23, 42);
  pdf.text('VantixMT — Downtime Report', margin, margin + 6);
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  pdf.text(
    `Period: ${preset}  •  Generated: ${new Date().toLocaleString()}`,
    margin,
    margin + 12,
  );

  const imgY = margin + 18;

  // If the image fits on one page, place it directly. Otherwise paginate.
  if (imgH <= pageH - imgY - margin) {
    pdf.addImage(imgData, 'PNG', margin, imgY, printW, imgH);
  } else {
    let srcY     = 0;
    let firstPage = true;
    const pxPerMm = canvas.width / printW;

    while (srcY < canvas.height) {
      const availH   = firstPage ? pageH - imgY - margin : pageH - margin * 2;
      const sliceH   = availH * pxPerMm;
      const actualH  = Math.min(sliceH, canvas.height - srcY);
      const destH    = actualH / pxPerMm;

      const slice    = document.createElement('canvas');
      slice.width    = canvas.width;
      slice.height   = actualH;
      slice.getContext('2d')!.drawImage(canvas, 0, srcY, canvas.width, actualH, 0, 0, canvas.width, actualH);

      if (!firstPage) pdf.addPage();
      pdf.addImage(
        slice.toDataURL('image/png'),
        'PNG',
        margin,
        firstPage ? imgY : margin,
        printW,
        destH,
      );

      srcY     += actualH;
      firstPage = false;
    }
  }

  pdf.save(`downtime_report_${preset}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Date preset helpers ───────────────────────────────────────────────────────

type Preset = '30d' | '90d' | '6m' | '12m';

function presetDates(preset: Preset): { start_date: string; end_date: string } {
  const end  = new Date();
  const start = new Date();
  if (preset === '30d')  start.setDate(end.getDate() - 30);
  if (preset === '90d')  start.setDate(end.getDate() - 90);
  if (preset === '6m')   start.setMonth(end.getMonth() - 6);
  if (preset === '12m')  start.setMonth(end.getMonth() - 12);
  return {
    start_date: start.toISOString(),
    end_date:   end.toISOString(),
  };
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, icon: Icon, color,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: typeof Clock;
  color: 'amber' | 'sky' | 'indigo' | 'green';
}) {
  const chip = {
    amber:  'bg-amber-700',
    sky:    'bg-sky-700',
    indigo: 'bg-brand-700',
    green:  'bg-green-700',
  }[color];
  const border = {
    amber:  'border-l-amber-600',
    sky:    'border-l-sky-600',
    indigo: 'border-l-brand-600',
    green:  'border-l-green-600',
  }[color];
  return (
    <div className={`bg-white rounded-lg border border-canvas-line border-l-4 ${border} px-5 py-4 flex items-start gap-3`}>
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0 text-white ${chip}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] text-slate-600 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-semibold font-mono tracking-tight text-slate-900">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const [preset, setPreset]           = useState<Preset>('90d');
  const [equipmentId, setEquipmentId] = useState('');
  const [pdfLoading, setPdfLoading]   = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const dates = presetDates(preset);

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'downtime', preset, equipmentId],
    queryFn:  () => reportsApi.downtime({
      ...dates,
      ...(equipmentId ? { equipment_id: equipmentId } : {}),
    }),
  });

  const { data: equipment } = useQuery({
    queryKey: ['equipment', 'all'],
    queryFn:  () => equipmentApi.list(1, 200),
  });

  const summary = data?.summary;

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      await exportPdf(reportRef, preset);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">

      {/* ── Filters + exports (top of page, drives every section below) ──── */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select
          value={preset}
          onChange={e => setPreset(e.target.value as Preset)}
          className="w-36 py-1.5 text-sm"
        >
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="6m">Last 6 months</option>
          <option value="12m">Last 12 months</option>
        </Select>

        <Select
          value={equipmentId}
          onChange={e => setEquipmentId(e.target.value)}
          className="w-52 py-1.5 text-sm"
        >
          <option value="">All equipment</option>
          {equipment?.data.map(eq => (
            <option key={eq.id} value={eq.id}>{eq.asset_tag} — {eq.name}</option>
          ))}
        </Select>

        {data && (
          <div className="flex items-center gap-2">
            {(['Excel', 'CSV'] as const).map(fmt => (
              <Button
                key={fmt}
                variant="outline"
                size="sm"
                onClick={() => fmt === 'Excel' ? exportExcel(data, preset) : exportCsv(data, preset)}
                title={`Download ${fmt} — summary + full incident log`}
              >
                <Download size={14} /> {fmt}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={handlePdf}
              disabled={pdfLoading}
              title="Download PDF with charts and incident log"
            >
              <FileText size={14} /> {pdfLoading ? 'Generating…' : 'PDF'}
            </Button>
          </div>
        )}
      </div>

      {/* ── KPIs (Pro tier — hidden for Standard SaaS) ─────────────────────── */}
      <KpiPanel startDate={dates.start_date} endDate={dates.end_date} />

      {isLoading && (
        <p className="text-center text-slate-400 py-16">Loading report…</p>
      )}

      {!isLoading && data && (
        <div ref={reportRef} className="flex flex-col gap-5">
          {/* ── KPI cards ────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label="Total Downtime"
              value={fmtMins(summary?.total_downtime_minutes ?? 0)}
              icon={Clock}
              color="amber"
            />
            <KpiCard
              label="Incidents"
              value={String(summary?.total_incidents ?? 0)}
              icon={AlertTriangle}
              color="sky"
            />
            <KpiCard
              label="Avg Per Incident"
              value={fmtMins(summary?.avg_downtime_per_incident ?? 0)}
              icon={Activity}
              color="indigo"
            />
            <KpiCard
              label="Total Cost"
              value={summary?.total_cost ? `$${summary.total_cost.toLocaleString()}` : '—'}
              sub="from completed work orders"
              icon={DollarSign}
              color="green"
            />
          </div>

          {/* ── Charts row ───────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Downtime by equipment — bar chart */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader title="Downtime by Equipment (hours)" />
                <CardBody>
                  {data.by_equipment.length === 0 ? (
                    <p className="text-center text-slate-400 py-8 text-sm">No data for this period.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart
                        data={data.by_equipment.map(d => ({
                          name:  d.asset_tag || d.equipment_name,
                          hours: parseFloat((d.downtime_minutes / 60).toFixed(1)),
                          incidents: d.incidents,
                        }))}
                        margin={{ top: 4, right: 8, left: -8, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} unit="h" />
                        <Tooltip
                          formatter={(val: number) => [`${val}h`, 'Downtime']}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Bar dataKey="hours" radius={[3, 3, 0, 0]}>
                          {data.by_equipment.map((_entry, i) => (
                            <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </div>

            {/* Downtime by type — pie chart */}
            <Card>
              <CardHeader title="By Work Type" />
              <CardBody>
                {data.by_task_type.length === 0 ? (
                  <p className="text-center text-slate-400 py-8 text-sm">No data.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.by_task_type.map(d => ({
                          name:  d.task_type,
                          value: d.downtime_minutes,
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {data.by_task_type.map((d, i) => (
                          <Cell key={i} fill={TASK_TYPE_COLORS[d.task_type] ?? '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val: number) => [fmtMins(val), 'Downtime']}
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Legend
                        formatter={(value: string) =>
                          value.charAt(0).toUpperCase() + value.slice(1)
                        }
                        iconSize={10}
                        wrapperStyle={{ fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Monthly trend — line chart */}
          <Card>
            <CardHeader title="Monthly Downtime Trend (hours)" />
            <CardBody>
              {data.by_month.length < 2 ? (
                <p className="text-center text-slate-400 py-8 text-sm">
                  Not enough data for a trend — needs at least 2 months of incidents.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={data.by_month.map(d => ({
                      month:  fmtMonth(d.month),
                      hours:  parseFloat((d.downtime_minutes / 60).toFixed(1)),
                      incidents: d.incidents,
                    }))}
                    margin={{ top: 4, right: 16, left: -8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="h" />
                    <Tooltip
                      formatter={(val: number, name: string) => [
                        name === 'hours' ? `${val}h` : val,
                        name === 'hours' ? 'Downtime' : 'Incidents',
                      ]}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone" dataKey="hours" name="hours"
                      stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone" dataKey="incidents" name="incidents"
                      stroke="#f97316" strokeWidth={2} strokeDasharray="4 2"
                      dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>

          {/* ── Downtime log table ────────────────────────────────────────── */}
          <Card>
            <CardHeader title={`Downtime Log (${data.log.length} incidents)`} />
            <CardBody className="p-0">
              {data.log.length === 0 ? (
                <p className="px-5 py-8 text-center text-slate-400 text-sm">
                  No downtime recorded for this period. Record downtime on a work order by filling in the "Downtime (min)" field when closing it.
                </p>
              ) : (
                <Table>
                  <Thead>
                    <tr>
                      <Th>WO #</Th>
                      <Th>Equipment</Th>
                      <Th>Title</Th>
                      <Th>Type</Th>
                      <Th>Date</Th>
                      <Th>Downtime</Th>
                      <Th>Cost</Th>
                      <Th>Status</Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {data.log.map((entry: DowntimeLogEntry) => (
                      <Tr key={entry.id}>
                        <Td className="font-mono text-xs">{entry.wo_number}</Td>
                        <Td className="text-xs">{entry.asset_tag} — {entry.equipment_name}</Td>
                        <Td className="max-w-[12rem] truncate font-medium">{entry.title}</Td>
                        <Td>
                          {entry.task_type ? (
                            <Badge color={
                              entry.task_type === 'corrective' ? 'red' :
                              entry.task_type === 'preventive' ? 'blue' : 'purple'
                            }>
                              {entry.task_type}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </Td>
                        <Td className="text-xs">{fmtDate(entry.actual_end)}</Td>
                        <Td className="font-medium text-red-600">
                          {fmtMins(entry.downtime_minutes)}
                        </Td>
                        <Td className="text-xs">
                          {entry.cost !== null ? `$${entry.cost.toLocaleString()}` : '—'}
                        </Td>
                        <Td>
                          <Badge color={
                            entry.status === 'approved' || entry.status === 'closed' ? 'green' :
                            entry.status === 'rejected' ? 'red' : 'slate'
                          }>
                            {entry.status.replace(/_/g, ' ')}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
